import { AgreeToTermsPolicy } from '@logto/schemas';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import { useContext } from 'react';
import { Outlet } from 'react-router-dom';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import DynamicT from '@/shared/components/DynamicT';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import CustomContent from '../AppLayout/CustomContent';

import TypedMotto from './TypedMotto';
import teraMark from './assets/tera-mark.svg';
import styles from './index.module.scss';

type BarLink = { key: TFuncKey; href: string };

/**
 * One white page over a faint dotted grid: a large grey Tera mark off the top-left corner, a pale
 * petal burst off the bottom-right. The left column carries the organisation's crest, name
 * (`branding.splitHeadline`) and motto (`branding.splitSubtitle`) — all supplied by the
 * organisation override when the sign-in request names an organisation — with a "Powered by Tera"
 * pill; the right column carries the form. A bar across the bottom holds Terms, Privacy and Support
 * (each only when configured) and the copyright.
 */
const MinimalLayout = () => {
  const { experienceSettings, theme } = useContext(PageContext);

  if (!experienceSettings) {
    return null;
  }

  const {
    branding,
    color,
    termsOfUseUrl,
    privacyPolicyUrl,
    supportWebsiteUrl,
    supportEmail,
    agreeToTermsPolicy,
  } = experienceSettings;
  const crest = getBrandingLogoUrl({ theme, branding, isDarkModeEnabled: color.isDarkModeEnabled });
  const name = branding.splitHeadline;
  const motto = branding.splitSubtitle;
  const supportHref = supportWebsiteUrl ?? (supportEmail ? `mailto:${supportEmail}` : undefined);

  const barLinks: BarLink[] = [
    ...(termsOfUseUrl ? [{ key: 'description.terms_of_use' as const, href: termsOfUseUrl }] : []),
    ...(privacyPolicyUrl
      ? [{ key: 'description.privacy_policy' as const, href: privacyPolicyUrl }]
      : []),
    ...(supportHref ? [{ key: 'description.support' as const, href: supportHref }] : []),
  ];

  return (
    <div className={classNames(styles.viewBox, theme === 'dark' && styles.dark)}>
      <div className={classNames(styles.page, layoutClassNames.pageContainer)}>
        {/* Both marks live in the stylesheet: the experience CSP blocks inline style attributes. */}
        <div className={styles.teraMark} />
        <div className={styles.burst} />
        {/* Aurora across the whole page, drawn from the tenant's brand colour. */}
        <div className={styles.aurora}>
          <span />
          <span />
          <span />
          <span />
        </div>
        <div className={styles.columns}>
          <aside className={styles.brand}>
            <div className={styles.logoRow}>
              {crest && <img className={styles.crest} src={crest} alt={name ?? ''} />}
              {name && <span className={styles.name}>{name}</span>}
              <span className={styles.pill}>
                <img src={teraMark} alt="" />
                <span>
                  <DynamicT forKey="description.powered_by" />
                </span>
                <b>Tera</b>
              </span>
            </div>
            {motto && <TypedMotto text={motto} />}
          </aside>
          <div className={styles.formColumn}>
            <main className={classNames(styles.main, layoutClassNames.mainContent)}>
              <CustomContent className={layoutClassNames.customContent} />
              <Outlet />
              {/* The automatic agreement sits under the form; the pages skip their own copy on this
                  layout (useLayoutRendersTerms). The manual-agreement checkbox stays in the form. */}
              {agreeToTermsPolicy === AgreeToTermsPolicy.Automatic && (
                <TermsAndPrivacyLinks className={styles.terms} />
              )}
            </main>
          </div>
        </div>
        <footer className={styles.bar}>
          {barLinks.length > 0 && (
            <nav className={styles.links}>
              {barLinks.map(({ key, href }) => (
                <a key={key} href={href} target="_blank" rel="noopener noreferrer">
                  <DynamicT forKey={key} />
                </a>
              ))}
            </nav>
          )}
          {name && (
            <span className={styles.copyright}>
              © {new Date().getFullYear()} {name}
            </span>
          )}
        </footer>
      </div>
    </div>
  );
};

export default MinimalLayout;
